import { isEqual } from "lodash"
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useEdges, useNodes, useReactFlow } from "reactflow"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import { useIsMobile } from "../../../../common/utils"
import {
  selectSaveButtonClicked,
  updateIsSaving,
  updateSaveButtonClicked,
  updateWorkflowHasChanged,
} from "../reducer"
import NODE_TYPES from "./react-flow-nodes/types"

/**
 * Do things here that should be done in WorkflowToolbar but couldn't because of ReactFlow context
 */
export default function GlobalState({ workflow }) {
  const nodes = useNodes()
  const edges = useEdges()
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()
  const shouldSave = useSelector(selectSaveButtonClicked)
  const { setViewport } = useReactFlow()
  const isMobile = useIsMobile()
  const workflowLoaded = Object.keys(workflow).length > 0

  useEffect(
    () => {
      if (!workflowLoaded) {
        return
      }
      const entryConditionNode = nodes[0]
      if (!entryConditionNode) {
        return
      }
      const entryConditionNodePosition = entryConditionNode.position
      setViewport({
        x: entryConditionNodePosition.x + (isMobile ? -200 : 350),
        y: entryConditionNodePosition.y + (isMobile ? 70 : 100),
        zoom: 1.2,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workflowLoaded]
  )

  useEffect(
    () => {
      if (!workflowLoaded) {
        return
      }
      const workflowConfig = workflow.config
      const workflowConfigNodes = workflowConfig.nodes
      const nodeCountChanged = workflowConfigNodes.length !== nodes.length
      // compare only properties of interest (from workflowconfig as these do not include extra properties internal to reactflow)
      let anyNodeChangedInternally = false
      if (!nodeCountChanged) {
        anyNodeChangedInternally = !workflowConfigNodes
          .map((node, nodeIndex) =>
            Object.keys(node)
              .map((key) => isEqual(node[key], nodes[nodeIndex][key]))
              .reduce((keyEqual, acc) => keyEqual && acc, true)
          )
          .reduce((cur, acc) => cur && acc, true)
      }
      dispatch(
        updateWorkflowHasChanged(
          nodeCountChanged ||
            anyNodeChangedInternally ||
            // reactflow doesn't add extra properties to edges it seems so can just use isEqual
            !isEqual(workflowConfig.edges, edges)
        )
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, edges, workflowLoaded]
  )

  useEffect(
    () => {
      if (!shouldSave) {
        return
      }
      dispatch(updateIsSaving(true))
      dispatch(updateSaveButtonClicked(false))
      // remove extra email campaign data
      const strippedNodes = nodes.map((node) =>
        [NODE_TYPES.email].indexOf(node.type) > -1
          ? {
              ...node,
              data: {
                ...node.data,
                editorData: { id: node.data.editorData?.id },
              },
            }
          : node
      )
      axios
        .put(`/workflows/api/workflows/${workflow.id}/`, {
          ...workflow,
          config: { nodes: strippedNodes, edges },
        })
        .then(() => dispatch(updateWorkflowHasChanged(false)))
        .finally(() => dispatch(updateIsSaving(false)))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldSave]
  )

  return null
}
